import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { toObservable } from '@angular/core/rxjs-interop'
import { catchError, map, share, shareReplay, switchMap, tap } from 'rxjs/operators'
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs'

import { environment } from '@env'
import { FormMode } from '@core/form/form.enums'
import { LazyParams } from '@shared/components/table/table.models'
import { SortingService } from '@core/services/sorting/sorting.service'
import { FilterOption } from '@shared/components/filters/filters.models'

import { User } from '@features/user-customers/users/users.models'
import { Customer, CustomerDetail } from '@features/user-customers/customers/customers.models'
import { getProfileTypeTranslation } from '@features/user-customers/users/users.utils'
import { PermissionManagementService } from '../permission-management/permission-management.service'
import { logError } from '@shared/opretators/log-error.operator'
import { isBase64 } from '@core/services/file/file.utils'
import { catchRequestError, completeWhen } from '@performance/ui'
import { ProfileStore } from '@core/store/profile/profile.store'
import { setCustomTenant } from '@core/requests/tenant.interceptor'
import { sortBy } from 'lodash-es'

@Injectable({
  providedIn: 'root',
})
export class UserCustomerViewService {
  readonly #http = inject(HttpClient)
  readonly #sortingService = inject(SortingService)
  readonly #permissionManagementService = inject(PermissionManagementService)
  readonly #profileStore = inject(ProfileStore)

  profileTypeOptionsSubject$ = new BehaviorSubject<FilterOption[]>(null)
  profileTypeOptions$: Observable<FilterOption[]>
  positionOptions$: Observable<FilterOption[]>

  newCustomer$ = new Subject<void>()

  readonly #userId$ = toObservable(this.#profileStore.userId)

  getUsers$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    const paramsBackEnd = {
      filtering: {
        freeText: '',
        filterQuality: 'WithProfiles',
        ...params,
      },
      ...lazyParams,
    }
    return this.#http.post<any>(environment.API.SEARCH_USERS, paramsBackEnd).pipe(
      map((data) => ({
        ...data,
        sorting: {
          ...data.sorting,
          ...lazyParams.sorting,
        },
        users: this.#sortingService.sort(
          data.users.map((user) => ({
            ...user,
            ...(paramsBackEnd.filtering.filterQuality === 'WithProfiles'
              ? {
                  customerName: user.customer.name,
                  ...user.profiles.reduce((obj, { id }) => ({ ...obj, [id]: true }), {}),
                }
              : {}),
          })),
        ),
      })),
      share(),
      logError(),
    )
  }

  getUsersById(ids: string[]) {
    return this.#http
      .get<any>(environment.API.USERS, {
        params: { ids },
      })
      .pipe(
        map((users) =>
          users.map((user) => ({
            ...user,
            photoPath: user.userAvatar && user.userAvatar.avatarPath,
            customerId: user.customer.id,
            tenantId: user.customer.calculatedTenantId,
            profileIds: user.profiles.map(({ id }) => id),
          })),
        ),
        share(),
        logError(),
      )
  }

  getUserById(id?: string) {
    return this.getUsersById(id ? [id] : []).pipe(map((res) => res[0]))
  }

  updateUser$(user: User) {
    user = {
      ...user,
      userAvatar: {
        ...user.userAvatar,
        encodedImage: isBase64(user.photoPath) ? user.photoPath : null,
      },
      photoPath: undefined,
    }
    return this.#http.put<any>(environment.API.USERS, [user])
  }

  getUserProfileTypes$() {
    return this.#http.get<any>(environment.API.USER_PROFILE_TYPES).pipe(share(), logError())
  }

  getUserProfileTypeOptions$(forceFetch?: boolean) {
    if (this.profileTypeOptionsSubject$.value && !forceFetch) {
      return of(this.profileTypeOptionsSubject$.value)
    } else {
      return !forceFetch && this.profileTypeOptions$
        ? this.profileTypeOptions$
        : (this.profileTypeOptions$ = this.getUserProfileTypes$().pipe(
            map((data) =>
              data.map(({ id, name }) => ({ label: getProfileTypeTranslation(name), value: id })),
            ),
            tap((data: FilterOption[]) => this.profileTypeOptionsSubject$.next(data)),
          ))
    }
  }

  deactivateUsersById(ids: string[]) {
    const paramsBackEnd = {
      userIds: ids,
      active: false,
    }
    return this.#http.post<any>(environment.API.USERS_ACTIVATIONS, paramsBackEnd)
  }

  renewUsersById(id: string[], renewalDate) {
    const paramsBackEnd = {
      userIds: id,
      activePeriodEnd: renewalDate,
    }
    return this.#http.post<any>(environment.API.USERS_LICENSES, paramsBackEnd)
  }

  getCustomers$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    const paramsBackEnd = {
      filtering: {
        filterQuality: 'withPrincipalCustomer',
        ...params,
      },
      ...lazyParams,
    }

    return this.#http.post<any>(environment.API.SEARCH_CUSTOMERS, paramsBackEnd).pipe(
      map((data) => ({
        ...data,
        sorting: {
          ...data.sorting,
          ...lazyParams.sorting,
        },
        customers: this.#sortingService.sort(
          data.customers.map((customer) => ({
            ...customer,
            principalId: (customer.principal || {}).id,
            principalIdReadOnly: (customer.principal || {}).id,
            mainCustomer: (customer.principal || {}).name,
          })),
        ),
      })),
      share(),
      logError(),
    )
  }

  getCustomerOptions$() {
    return this.getCustomers$({ filterQuality: 'raw' }).pipe(
      map((data) =>
        data.customers
          .map(({ id, name }) => ({ label: name, value: id }))
          .sort((a, b) => {
            const labels = [a.label.toUpperCase(), b.label.toUpperCase()]
            const checkLabels = labels[0] > labels[1] ? 1 : 0
            return labels[0] < labels[1] ? -1 : checkLabels
          }),
      ),
    )
  }

  getPositionOptions$(forceFetch?: boolean) {
    return this.positionOptions$ && !forceFetch
      ? this.positionOptions$
      : (this.positionOptions$ = this.#http
          .get<{ id: string; name: string }[]>(environment.API.USER_POSITIONS)
          .pipe(
            map((positions) => positions.map(({ id, name }) => ({ label: name, value: id }))),
            shareReplay(1),
          ))
  }

  private updateCustomer(customer) {
    return this.#http.put<any>(`${environment.API.CRUD_CUSTOMER}/${customer.id}`, {
      ...customer,
      organizationId: null,
    })
  }

  private createCustomer(customer) {
    return this.#userId$.pipe(
      completeWhen((id) => !!id),
      map((authenticatedUserId) => ({
        ...customer,
        authenticatedUserId,
      })),
      switchMap((payload) => this.#http.post<any>(environment.API.CRUD_CUSTOMER, payload)),
    )
  }

  createOrUpdateCustomer(customer, type = FormMode.Edit) {
    return (
      type === FormMode.Edit ? this.updateCustomer(customer) : this.createCustomer(customer)
    ).pipe(
      map((customerItem: Customer) => ({
        ...customerItem,
        principalIdReadOnly: customerItem.principalId,
      })),
    )
  }

  getRequesterUserOptions$(params = {}, lazyParams: LazyParams = {} as LazyParams) {
    return this.getUsers$(params, lazyParams).pipe(
      map(({ users }) => {
        return users.map((user) => ({
          ...user,
          requesterUser: { requesterName: user.fullName, requesterEmail: user.email },
          shownValue: `${user.fullName} (${user.email}) | ${user.customerName}`,
        }))
      }),
      catchError(() => of([])),
    )
  }

  getCategoryGroupOptions$() {
    return this.#permissionManagementService.getCategoryGroupOptions$()
  }

  revokePassword(userIds: string[]): Observable<any> {
    return this.#http.post(environment.API.USER_PASSWORD_EXPIRATION, { userIds }).pipe(
      catchError((err: any) => {
        console.error(err)
        return throwError(() => new Error(err))
      }),
    )
  }

  resendWelcomeEmail(userIds: string[]): Observable<any> {
    return this.#http.post(environment.API.USER_RESEND_WELCOME_EMAIL, { userIds }).pipe(
      catchError((err: any) => {
        console.error(err)
        return throwError(() => new Error(err))
      }),
    )
  }

  deleteUser(userId: string): Observable<any> {
    return this.#http.delete(`${environment.API.USERS}/${userId}`)
  }

  getCustomerById(customerId: string): Observable<any> {
    return this.#http.get<CustomerDetail>(`${environment.API.CRUD_CUSTOMER}/${customerId}`).pipe(
      map((customer) => ({
        ...customer,
        users: sortBy(customer.users, 'activePeriodEnd'),
        areNotCollaboratingUsers: !customer.areCollaboratingUsers,
        collaborators: (customer.collaborators || []).filter(Boolean),
      })),
      catchRequestError(),
    )
  }

  getCustomerAllowedClients(customer?: CustomerDetail, tenantId?: string): Observable<any> {
    let filtering: any = {
      freeText: '',
      filterQuality: 0,
    }

    if (customer) {
      filtering = {
        ...filtering,
        excludeCollaboratorsOfCustomerId: customer.id,
      }
    }

    return this.#http.post(
      environment.API.SEARCH_CUSTOMERS,
      {
        filtering,
        paging: {
          recordsRange: {
            start: 0,
            end: 1000,
          },
        },
        sorting: {
          column: 0,
          direction: 0,
        },
      },
      { context: setCustomTenant(tenantId) },
    )
  }
}
